import { useState } from "react";
import { ACCESS_POLICIES, levelOf, policyLabel } from "../../../lib/access";
import { PERMISSION_GROUPS, knownPermissions } from "../../../lib/organization";
import { useRoleForm } from "../../SuperAdmin/useRoleForm";
import { Avatar, Btn, Field, INPUT, Tag, cx } from "../../../components/msd/ui";
import { ConfirmDelete, Picker, RemoveBtn, plural } from "./kit";

// Laid out like a phone's settings: a grouped list, the name on the left and its access on
// the right, picked from one menu for every permission. "read" is shown as View: it is what
// the person can do with the page, not how it is stored.
const LEVELS = [
  ["none", "No access"],
  ["read", "View"],
  ["edit", "Edit"],
];
const ALLOW = [
  ["none", "No access"],
  ["on", "Allow"],
];

// What each feature opens: the hover text on its name.
const FEATURE_HELP = {
  advisors_desk: "Chat with customers in their service groups.",
  chat_audit: "Read past group chats and what happened in them.",
  book_service: "Book a service and start its WhatsApp group.",
  workshop_analytics: "Workshop reports and charts.",
  system_health: "Message delivery and errors.",
  manage_regions: "The regions the brand is split into.",
  manage_dealers: "Dealerships and their details.",
  manage_workshops: "Workshops and their contacts.",
  manage_roles: "These roles and what each one can do.",
  manage_people: "Logins and the role each person holds.",
};

const SECTION =
  "flex flex-col gap-3 border-t border-msd-line px-6 py-5 first:border-t-0";
// The page's inputs are sized for a form on its own; here they sit in a dense editor.
const SMALL_INPUT = cx(INPUT, "!h-9 !rounded-[10px] !px-3 !text-[14px]");

const SECTION_TITLE = "m-0 text-[14px] font-semibold text-msd-ink";

const same = (role, form) => {
  const pick = (r) =>
    JSON.stringify([
      r.label || "",
      r.short_label || "",
      r.code || "",
      r.access_policy,
      [...knownPermissions(r)].sort(),
    ]);
  return pick(role) === pick(form);
};

/** One role, edited in place on the right of Roles and access: the same form and
 *  write as Super Admin's role drawer (useRoleForm). `onDone` gets the role's code
 *  after a save, or nothing after a delete or a discarded new role. `dirtyRef` tells
 *  the list whether leaving now would lose changes. */
export default function RolePane({
  role,
  people,
  canEdit,
  onDone,
  dirtyRef,
  onName,
  tenantId = null,
}) {
  const {
    form,
    set,
    errors,
    isNew,
    codeLocked,
    setLabel,
    setCode,
    toggle,
    setLevel,
    onSave,
    onDelete,
    pending,
  } = useRoleForm(role, tenantId, (deleted) =>
    onDone(deleted ? null : form.code),
  );
  const [confirming, setConfirming] = useState(false);
  const dirty = isNew || !same(role, form);
  dirtyRef.current = canEdit && dirty;

  const discard = () =>
    isNew
      ? onDone(null)
      : set({ ...role, permissions: knownPermissions(role) });

  return (
    <section
      aria-label={isNew ? "New role" : role.label}
      className="flex min-h-0 flex-col"
    >
      <header className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-t-[20px] bg-msd-surface px-6 pb-3 pt-4 shadow-[0_1px_0_var(--line)]">
        <h2 className="m-0 min-w-0 truncate text-[16px] font-semibold text-msd-ink">
          {isNew ? "New role" : role.label}
        </h2>
        {form.is_system && (
          <Tag
            small
            icon="lock"
            title="Built in: its code cannot change and it cannot be deleted"
          >
            Built-in
          </Tag>
        )}
        {canEdit && (
          // Save sits with the title, so it is in reach without scrolling to the end.
          <div className="ml-auto flex items-center gap-2">
            {dirty && (
              <Btn onClick={discard}>{isNew ? "Cancel" : "Discard"}</Btn>
            )}
            <Btn variant="pri" onClick={onSave} disabled={pending || !dirty}>
              {isNew ? "Create role" : "Save changes"}
            </Btn>
          </div>
        )}
      </header>

      {canEdit && (
        <div className={SECTION}>
          <div className="grid grid-cols-[minmax(0,280px)_120px_minmax(0,220px)] gap-3 max-[640px]:grid-cols-1">
            <Field label="Role name" error={errors.label}>
              <input
                type="text"
                className={SMALL_INPUT}
                value={form.label}
                placeholder="For example, Regional manager"
                onChange={(e) => {
                  setLabel(e.target.value);
                  if (isNew) onName(e.target.value);
                }}
              />
            </Field>
            <Field label="Short name" error={errors.short_label}>
              <input
                type="text"
                className={SMALL_INPUT}
                value={form.short_label}
                onChange={(e) => set({ short_label: e.target.value })}
              />
            </Field>
            <Field label="Role code" error={errors.code}>
              <input
                type="text"
                className={cx(SMALL_INPUT, codeLocked && "cursor-not-allowed")}
                value={form.code}
                disabled={codeLocked}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
          </div>
        </div>
      )}

      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Access scope</h3>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {canEdit ? (
            <div className="w-[240px] max-w-full">
              <Picker
                field
                label="Access scope"
                options={ACCESS_POLICIES.map((p) => [p.id, p.label])}
                value={form.access_policy}
                onChange={(v) => set({ access_policy: v })}
              />
            </div>
          ) : (
            <div className="text-[15px] font-semibold text-msd-ink">
              {policyLabel(form.access_policy)}
            </div>
          )}
        </div>
      </div>

      <div className={SECTION}>
        <h3 className={SECTION_TITLE}>Permissions</h3>
        {PERMISSION_GROUPS.map(({ label, items }) => (
          <div
            key={label || "_"}
            role="group"
            aria-label={label || "General"}
            className="max-w-[560px]"
          >
            <div className="px-4 pb-[6px] pt-2 text-[12px] font-medium uppercase tracking-[.04em] text-msd-muted">
              {label || "General"}
            </div>
            <div className="rounded-[12px] bg-msd-ground">
              {items.map((p) => {
                const level = p.levelled
                  ? levelOf(form.permissions, p.id)
                  : form.permissions.includes(p.id);
                return (
                  <div
                    key={p.id}
                    className="flex min-h-[46px] items-center gap-3 border-t border-msd-line px-4 py-[6px] first:border-t-0"
                  >
                    <span
                      title={FEATURE_HELP[p.id]}
                      className="min-w-0 grow text-[14.5px] text-msd-ink"
                    >
                      {p.label}
                    </span>
                    <Picker
                      label={p.label}
                      options={p.levelled ? LEVELS : ALLOW}
                      value={
                        p.levelled ? level || "none" : level ? "on" : "none"
                      }
                      disabled={!canEdit}
                      onChange={(v) =>
                        p.levelled
                          ? setLevel(p.id, v === "none" ? false : v)
                          : toggle(p.id)
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {errors.permissions && (
          <span className="text-[12.5px] text-msd-brick-ink" role="alert">
            {errors.permissions}
          </span>
        )}
      </div>

      {people && !isNew && (
        <div className={SECTION}>
          <h3 className={SECTION_TITLE}>Members</h3>
          {people.length ? (
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
              {people.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center gap-2 rounded-full bg-msd-soft py-1 pl-1 pr-3 text-[13px] text-msd-ink2"
                >
                  <Avatar name={p.name || p.email} size={24} />
                  {p.name || p.email}
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-[13px] text-msd-muted">No members</p>
          )}
        </div>
      )}

      {canEdit && !isNew && !form.is_system && (
        <div className="border-t border-msd-line px-6 py-4">
          <RemoveBtn onClick={() => setConfirming(true)} disabled={pending}>
            Delete role
          </RemoveBtn>
        </div>
      )}

      <ConfirmDelete
        open={confirming}
        onClose={() => setConfirming(false)}
        name={role.label}
        body={`Nobody can be given ${role.label} after this. This cannot be undone.`}
        blocked={
          role.user_count
            ? `${plural(role.user_count, "person holds", "people hold")} it. Give them another role first.`
            : null
        }
        onConfirm={async () => {
          setConfirming(false);
          await onDelete();
        }}
        pending={pending}
      />
    </section>
  );
}
